import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AcademicAppointment,
  AcademicCalendarFilters,
  AcademicSchedulingOptions,
  CreateAcademicAppointmentDto,
  UpdateAcademicAppointmentDto,
} from '../../interfaces/academic-appointment';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

/**
 * Séances académiques du professeur connecté.
 *
 * Endpoints:
 *   GET    /api/professor/academic-appointments/calendar?from&to&subjectId&groupId&studentId
 *   GET    /api/professor/academic-appointments/options
 *   GET    /api/professor/academic-appointments/{id}
 *   POST   /api/professor/academic-appointments
 *   PUT    /api/professor/academic-appointments/{id}
 *   DELETE /api/professor/academic-appointments/{id}
 */
@Injectable({ providedIn: 'root' })
export class ProfessorAcademicAppointmentService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.BASE_URL}/professor/academic-appointments`;

  /** Calendrier du prof. Chaque filtre non renseigné laisse passer tout le reste. */
  myCalendar(
    filters: AcademicCalendarFilters = {},
  ): Observable<ApiCollectionResponse<AcademicAppointment>> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.subjectId) params = params.set('subjectId', filters.subjectId);
    if (filters.groupId) params = params.set('groupId', filters.groupId);
    if (filters.studentId) params = params.set('studentId', filters.studentId);
    if (filters.includeCancelled) params = params.set('includeCancelled', true);

    return this.http.get<ApiCollectionResponse<AcademicAppointment>>(
      `${this.base}/calendar`,
      { params },
    );
  }

  /** Matières enseignées, groupes encadrés et élèves — pour le formulaire de séance. */
  options(): Observable<ApiSingleResponse<AcademicSchedulingOptions>> {
    return this.http.get<ApiSingleResponse<AcademicSchedulingOptions>>(
      `${this.base}/options`,
    );
  }

  getById(id: number): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.get<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}`,
    );
  }

  create(
    dto: CreateAcademicAppointmentDto,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.post<ApiSingleResponse<AcademicAppointment>>(this.base, dto);
  }

  update(
    id: number,
    dto: UpdateAcademicAppointmentDto,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.put<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}`,
      dto,
    );
  }

  /** Annulation logique : la séance passe en `Cancelled` et quitte les calendriers. */
  cancel(
    id: number,
    reason: string,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.delete<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}`,
      { body: { reason } },
    );
  }

  /**
   * Déclare qu'une séance de production a eu lieu — ou non.
   *
   * Le serveur ne l'accepte que sur une séance sans groupe ni élève : ailleurs,
   * c'est le journal d'accès qui atteste la tenue.
   */
  declareOutcome(
    id: number,
    held: boolean,
    notes?: string | null,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.post<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}/outcome`,
      { held, notes: notes?.trim() || null },
    );
  }
}
